import { connect, ConnectedProps } from 'react-redux';
import { useNavigate } from 'react-router';
import { Action, Dispatch } from 'redux';

import { PaymentStatusEnum } from '../../../lib/enums/payments/payment-status.enum';
import lang from '../../../lib/language';
import { RootState } from '../../../lib/store';
import { hasAnyAccountPaidPlan } from '../../../lib/store/crm/helpers';
import { getSolanaPayments } from '../../../lib/store/payment';

import { MobileDrawer } from '../../../components';
import { useIsMobile } from '../../../hooks';
import { setSolanaPendingPlanPayment } from '../../../hooks/useSolanaPendingPlanPayment';
import BaseModal from '../../../shared_components/BaseModal/BaseModal';
import Button from '../../../shared_components/Button/Button';
import { ButtonTypesEnum } from '../../../shared_components/Button/Button.types';
import { UI_ROUTES } from '../../../util/routes';

import styles from './CryptoPaymentModal.module.scss';

type OwnProps = {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  isAutoPass?: boolean;
  isPlanPurchase?: boolean;
};

type Props = OwnProps & ReduxProps;

const CryptoPaymentModal = ({
  isOpen,
  setIsOpen,
  isAutoPass,
  isPlanPurchase,
  fetchSolanaPayments,
  hasPaidPlan,
  totalLossReached,
}: Props) => {
  const navigate = useNavigate();
  const { isMobile } = useIsMobile();
  const pageSize = !hasPaidPlan || totalLossReached ? 1 : 2;

  const continueToPlatform = () => {
    fetchSolanaPayments(pageSize);
    if (isAutoPass) {
      navigate(UI_ROUTES.planDetails);
    } else {
      if (isPlanPurchase) {
        setSolanaPendingPlanPayment(true);
      }
      navigate(UI_ROUTES.dashboard);
    }
  };

  const renderContent = () => (
    <>
      <div className={styles.text}>{lang.cryptoPaymentModalDescription()}</div>
      <div className={styles.text}>{lang.cryptoPaymentModalDescription2()}</div>
      <div className={styles.buttonsWrapper}>
        <Button fullWidth biggerFontSize onClick={continueToPlatform}>
          {lang.commonContinuePlatformButton()}
        </Button>
        <Button
          fullWidth
          biggerFontSize
          onClick={() => setIsOpen(false)}
          variant={ButtonTypesEnum.Secondary}
        >
          {lang.commonCancelButton()}
        </Button>
      </div>
    </>
  );

  if (isMobile) {
    return (
      <MobileDrawer
        isOpen={isOpen}
        title={lang.cryptoPaymentModalTitle()}
        onClose={() => setIsOpen(false)}
      >
        {renderContent()}
      </MobileDrawer>
    );
  }

  return (
    <BaseModal isOpen={isOpen} toggle={() => setIsOpen(false)}>
      <div className={styles.title}>{lang.cryptoPaymentModalTitle()}</div>
      {renderContent()}
    </BaseModal>
  );
};

const mapStateToProps = (state: RootState) => ({
  totalLossReached: state.closeout.accountStatusData.totalLossReached,
  hasPaidPlan: hasAnyAccountPaidPlan(state.crm),
});

const mapDispatchToProps = (dispatch: Dispatch<Action>) => ({
  fetchSolanaPayments: (pageSize: number) =>
    dispatch(
      getSolanaPayments({
        statuses: [
          PaymentStatusEnum.Processed,
          PaymentStatusEnum.Completed,
          PaymentStatusEnum.Pending,
          PaymentStatusEnum.Canceled,
          PaymentStatusEnum.Rejected,
          PaymentStatusEnum.CanceledByClient,
        ],
        pageSize,
      }),
    ),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default connector(CryptoPaymentModal);
